/**
 * Site content (CMS) — fetched once from /api/content and shared via Context.
 *
 * Usage:
 *   const { c } = useSiteContent();
 *   <h1>{c("home_hero_title_line1", "Le bar à cocktails")}</h1>
 *
 * The fallback (2nd arg) is used:
 *  - while loading on first render
 *  - if the key is unknown / fetch fails
 */
import { createContext, useContext, useEffect, useState } from "react";
import api from "../lib/api";

const SiteContentContext = createContext({ content: {}, loaded: false });

export function SiteContentProvider({ children }) {
  const [content, setContent] = useState({});
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/content")
      .then(({ data }) => setContent(data || {}))
      .catch(() => { /* fallback to defaults silently */ })
      .finally(() => setLoaded(true));
  }, []);

  return (
    <SiteContentContext.Provider value={{ content, loaded }}>
      {children}
    </SiteContentContext.Provider>
  );
}

export function useSiteContent() {
  const { content, loaded } = useContext(SiteContentContext);
  const c = (key, fallback = "") => {
    if (content && typeof content[key] === "string" && content[key].length > 0) {
      return content[key];
    }
    return fallback;
  };
  return { c, content, loaded };
}
